// What something is and how far along: spinners, badges (always in words), meters, progress, avatars and dots.
import type { ReactNode } from 'react';
import { cx } from './cx';
import s from './Status.module.css';
import { initials } from './text';

/** Something is happening. Its label is read by a screen reader; pass `label={null}` where words beside it say it. */
export function Spinner({
  size = 'regular',
  label = 'Loading',
}: {
  size?: 'small' | 'regular' | 'large';
  label?: string | null;
}) {
  return (
    <span role={label ? 'status' : undefined} className={s.spinnerWrap}>
      <span
        aria-hidden="true"
        className={cx(s.spinner, size === 'small' && s.spinnerSmall, size === 'large' && s.spinnerLarge)}
      />
      {label && <span className="visually-hidden">{label}</span>}
    </span>
  );
}

export type Tone = 'neutral' | 'accent' | 'warn' | 'bad' | 'info';

/** A short word about something: "Admin", "Waiting", "Expired". Never colour alone. */
export function Badge({ tone = 'neutral', icon, children }: { tone?: Tone; icon?: ReactNode; children: ReactNode }) {
  return (
    <span className={cx(s.badge, tone !== 'neutral' && s[tone])}>
      {icon}
      <span>{children}</span>
    </span>
  );
}

/**
 * How much of something is used, as a bar with its label and the amount in words: amber from 80%, red from 95%.
 */
export function Meter({
  label,
  value,
  max,
  valueText,
}: {
  label: string;
  value: number;
  max: number;
  /** The amount as read: "1.2 GB of 5 GB". */
  valueText: string;
}) {
  const share = max > 0 ? Math.min(Math.max(value / max, 0), 1) : 0;
  return (
    <div
      className={s.meter}
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.min(value, max)}
      aria-valuetext={valueText}
    >
      <div className={s.meterHead} aria-hidden="true">
        <span>{label}</span>
        <span className={s.meterValue}>{valueText}</span>
      </div>
      <div className={s.track} aria-hidden="true">
        <div
          className={cx(s.fill, share >= 0.95 ? s.fillBad : share >= 0.8 && s.fillWarn)}
          style={{ width: `${share * 100}%` }}
        />
      </div>
    </div>
  );
}

/** How far something has got; with no value, that it's going. */
export function ProgressBar({ label, value, valueText }: { label: string; value?: number; valueText?: string }) {
  const known = value !== undefined;
  return (
    <div
      className={cx(s.track, !known && s.indeterminate)}
      role="progressbar"
      aria-label={label}
      aria-valuemin={known ? 0 : undefined}
      aria-valuemax={known ? 100 : undefined}
      aria-valuenow={known ? Math.round(value * 100) : undefined}
      aria-valuetext={valueText}
    >
      <div className={s.fill} style={known ? { width: `${Math.min(Math.max(value, 0), 1) * 100}%` } : undefined} />
    </div>
  );
}

/** Someone's initials in a circle. The name is said beside it, so a screen reader passes over it. */
export function Avatar({
  name,
  email,
  size = 'regular',
}: {
  name: string | null | undefined;
  email?: string | null;
  size?: 'small' | 'regular' | 'large';
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(s.avatar, size === 'large' && s.avatarLarge, size === 'small' && s.avatarSmall)}
    >
      {initials(name, email)}
    </span>
  );
}

/** A small dot beside words that say the same: an MFD seen lately, say. */
export function Dot({ tone = 'neutral' }: { tone?: 'neutral' | 'good' | 'warn' | 'bad' }) {
  return (
    <span
      aria-hidden="true"
      className={cx(s.dot, tone === 'good' && s.dotGood, tone === 'warn' && s.dotWarn, tone === 'bad' && s.dotBad)}
    />
  );
}
